import { memo, useCallback, useMemo, useState } from 'react'
import { getIcon } from '~/components/badge/ChannelBadge'
import { ChannelType } from '~/stores/stores.types'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandExpandContext,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
  Icon,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

const INITIAL_LIMIT = 200
const LOAD_MORE_INCREMENT = 200

export function ChannelsCommandList<T extends boolean = false>({
  selected = [],
  ...props
}: Props<T>) {
  const [condensedList, setCondensedList] = useState(true)
  const [displayLimit, setDisplayLimit] = useState(INITIAL_LIMIT)
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Channels)

  // Memoize filtered list to prevent recalculation on every render
  const filteredList = useMemo(() => {
    if (!condensedList) return data
    return data?.filter((channel) => {
      if (props.excludeIds) return !props.excludeIds.includes(channel.id)
      if (!props.contextFilters) return true
      const { id } = props.contextFilters
      if (id && '$eq' in id) return id.$eq.includes(channel.id)
      return true
    })
  }, [data, condensedList, props.excludeIds, props.contextFilters])

  const isSearching = query.trim().length > 0

  // Limit displayed items for performance
  const visibleItems = useMemo(() => {
    if (!isSearching) return filteredList?.slice(0, displayLimit)
    const normalizedQuery = query.toLowerCase()
    return filteredList?.filter((channel) =>
      channel.name.toLowerCase().includes(normalizedQuery),
    )
  }, [filteredList, displayLimit, query, isSearching])

  const remainingItems = (filteredList?.length || 0) - (visibleItems?.length || 0)

  const expandedResults = useMemo(
    () => (data?.length || 0) - (filteredList?.length || 0),
    [data?.length, filteredList?.length],
  )

  // Convert selected array to Set for O(1) lookups instead of O(n)
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const handleExpand = useCallback(() => setCondensedList(false), [])
  const handleLoadMore = useCallback(
    () => setDisplayLimit((prev) => prev + LOAD_MORE_INCREMENT),
    [],
  )

  const handleClear = useCallback(() => {
    props.onSelect([])
  }, [props.onSelect])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      props.onSelect(newSelection as unknown as string[])
    },
    [props.onSelect],
  )

  // Toggle a single item
  const handleToggleItem = useCallback(
    (id: string) => {
      if (props.multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        props.onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        props.onSelect([id])
      }
    },
    [props.multiselect, selected, props.onSelect],
  )

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder="Search channels"
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="mt-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={props.multiselect}
          />
          {props.allowEmpty && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              {props.allowEmptyLabel || 'Clear'}
            </CommandItem>
          )}
          {visibleItems?.map((channel) => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              selected={selectedSet.has(channel.id)}
              multiselect={props.multiselect}
              onToggle={handleToggleItem}
            />
          ))}
          {remainingItems > 0 && (
            <CommandExpandContext
              availableResults={remainingItems}
              optionalLabel="more channel"
              onSelect={handleLoadMore}
            />
          )}
          <CommandExpandContext
            availableResults={expandedResults}
            onSelect={handleExpand}
          />
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

interface Props<T extends boolean | undefined> extends BaseCommandListProps<T, string> {
  contextFilters?: {
    id?: {
      $eq: string[]
    }
  }
}

// Memoized channel item to prevent unnecessary re-renders
interface ChannelItemProps {
  channel: { id: string; name: string; meta: Record<string, string> }
  selected: boolean
  multiselect?: boolean
  onToggle: (id: string) => void
}

const ChannelItem = memo(function ChannelItem({
  channel,
  selected,
  multiselect,
  onToggle,
}: ChannelItemProps) {
  const handleToggle = useCallback(() => {
    onToggle(channel.id)
  }, [onToggle, channel.id])

  return (
    <CommandCheckItem
      value={channel.id}
      keywords={[channel.name]}
      onSelect={handleToggle}
      selected={selected}
      multiselect={multiselect}
    >
      <Icon {...getIcon(channel.meta.type as ChannelType)} stroke="0" />
      <span className="truncate">{channel.name}</span>
    </CommandCheckItem>
  )
})
